import { type Dispatch, type SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { type components } from "@jbnl/api-client";
import { CheckboxCard } from "@/components/CheckboxCard";
import { FormActions } from "@jbnl/shared-ui";
import { SelectAllCheckbox } from "@/components/SelectAllCheckbox";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FieldDescription, FieldLegend, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { datumKort } from "@/features/wedstrijden/datum";
import {
  useKlassen,
  type Wedstrijddag,
} from "@/features/wedstrijden/useWedstrijden";
import type { Boekjevelden, Wedstrijdboekje } from "./useWedstrijdboekjes";

type Klasse = components["schemas"]["KlasseDto"];

const toggle = (huidig: string[], value: string) =>
  huidig.includes(value)
    ? huidig.filter((x) => x !== value)
    : [...huidig, value];

/**
 * Every dag is offered, including those with no organiserend korps - the opposite of the
 * inschrijfperiode form, because a boekje only prints what is known and a dag still being
 * appointed is exactly what a wedstrijdleider wants to see in a proef.
 */
export function WedstrijdboekjeForm({
  id,
  boekje,
  dagen,
  submitLabel,
  pending,
  error,
  onSubmit,
}: {
  id: string;
  boekje?: Wedstrijdboekje;
  dagen: Wedstrijddag[];
  submitLabel: string;
  pending: boolean;
  error: Error | null;
  onSubmit: (velden: Boekjevelden) => void;
}) {
  const { t } = useTranslation(["wedstrijdboekjes", "common"]);
  const { data: klassen } = useKlassen();

  const [name, setName] = useState(boekje?.name ?? "");
  const [titel, setTitel] = useState(boekje?.titel ?? "");
  const [voettekst, setVoettekst] = useState(boekje?.voettekst ?? "");
  const [metStaandeTekst, setMetStaandeTekst] = useState(
    boekje?.metStaandeTekst ?? false,
  );
  const [wedstrijddagIds, setWedstrijddagIds] = useState<string[]>(
    boekje?.wedstrijddagIds ?? [],
  );

  // Empty means every klasse, so the checkbox is the state and the list is what it reveals.
  // A beheerder who ticked all four instead would get a boekje that silently stopped printing
  // everything the day JBNL added a fifth.
  const [klasseIds, setKlasseIds] = useState<string[]>(boekje?.klasseIds ?? []);
  const [alleKlassen, setAlleKlassen] = useState(
    (boekje?.klasseIds ?? []).length === 0,
  );

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          name,
          titel,
          voettekst,
          metStaandeTekst,
          wedstrijddagIds,
          klasseIds: alleKlassen ? [] : klasseIds,
        });
      }}
    >
      <div className="grid gap-2">
        <Label htmlFor={`${id}-naam`}>{t("form.naam")}</Label>
        <Input
          id={`${id}-naam`}
          value={name}
          maxLength={80}
          onChange={(event) => setName(event.target.value)}
          placeholder={t("form.naamPlaceholder")}
          required
        />
        <FieldDescription>{t("form.naamUitleg")}</FieldDescription>
      </div>

      <div className="grid gap-2">
        <Label htmlFor={`${id}-titel`}>{t("form.titel")}</Label>
        <Input
          id={`${id}-titel`}
          value={titel}
          maxLength={200}
          onChange={(event) => setTitel(event.target.value)}
          placeholder={t("form.titelPlaceholder")}
          required
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor={`${id}-voettekst`}>{t("form.voettekst")}</Label>
        <Input
          id={`${id}-voettekst`}
          value={voettekst}
          maxLength={200}
          onChange={(event) => setVoettekst(event.target.value)}
          placeholder={t("form.voettekstPlaceholder")}
          required
        />
        <FieldDescription>{t("form.voettekstUitleg")}</FieldDescription>
      </div>

      <div className="grid gap-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${id}-staande-tekst`}
            checked={metStaandeTekst}
            onCheckedChange={(checked) => setMetStaandeTekst(checked === true)}
          />
          <Label htmlFor={`${id}-staande-tekst`}>
            {t("form.staandeTekst")}
          </Label>
        </div>
        <FieldDescription>{t("form.staandeTekstUitleg")}</FieldDescription>
      </div>

      <WedstrijddagenVeld
        id={id}
        dagen={dagen}
        wedstrijddagIds={wedstrijddagIds}
        setWedstrijddagIds={setWedstrijddagIds}
      />

      <KlassenVeld
        id={id}
        klassen={klassen}
        klasseIds={klasseIds}
        setKlasseIds={setKlasseIds}
        alleKlassen={alleKlassen}
        setAlleKlassen={setAlleKlassen}
      />

      <FormActions>
        {error && <p className="text-sm text-destructive">{error.message}</p>}
        <Button type="submit" disabled={pending}>
          {pending ? t("common:actions.busy") : submitLabel}
        </Button>
      </FormActions>
    </form>
  );
}

function WedstrijddagenVeld({
  id,
  dagen,
  wedstrijddagIds,
  setWedstrijddagIds,
}: {
  id: string;
  dagen: Wedstrijddag[];
  wedstrijddagIds: string[];
  setWedstrijddagIds: Dispatch<SetStateAction<string[]>>;
}) {
  const { t } = useTranslation(["wedstrijdboekjes", "common"]);

  // Counted against what is on the screen rather than against the state, so "alles" means the
  // dagen this form is drawing and never a stale id the seizoen no longer offers.
  const gekozenDagen = dagen.filter((dag) => wedstrijddagIds.includes(dag.id));

  return (
    <FieldSet>
      <FieldLegend variant="label">{t("form.wedstrijddagen")}</FieldLegend>
      {dagen.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("form.geenDagen")}</p>
      ) : (
        <>
          <SelectAllCheckbox
            id={`${id}-dagen-alles`}
            total={dagen.length}
            selected={gekozenDagen.length}
            onChange={(alles) =>
              setWedstrijddagIds(alles ? dagen.map((dag) => dag.id) : [])
            }
          />
          <ul className="grid gap-2 sm:grid-cols-2">
            {dagen.map((dag) => (
              <li key={dag.id}>
                <CheckboxCard
                  id={`${id}-dag-${dag.id}`}
                  title={datumKort(dag.date)}
                  description={
                    dag.organiserendKorpsName
                      ? t("form.dagRegel", {
                          niveau: dag.niveauName,
                          korps: dag.organiserendKorpsName,
                        })
                      : t("form.dagZonderKorps", { niveau: dag.niveauName })
                  }
                  checked={wedstrijddagIds.includes(dag.id)}
                  onCheckedChange={() =>
                    setWedstrijddagIds((huidig) => toggle(huidig, dag.id))
                  }
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </FieldSet>
  );
}

function KlassenVeld({
  id,
  klassen,
  klasseIds,
  setKlasseIds,
  alleKlassen,
  setAlleKlassen,
}: {
  id: string;
  klassen: Klasse[] | undefined;
  klasseIds: string[];
  setKlasseIds: Dispatch<SetStateAction<string[]>>;
  alleKlassen: boolean;
  setAlleKlassen: Dispatch<SetStateAction<boolean>>;
}) {
  const { t } = useTranslation(["wedstrijdboekjes", "common"]);
  const gekozenKlassen = (klassen ?? []).filter((klasse) =>
    klasseIds.includes(klasse.id),
  );

  return (
    <FieldSet>
      <FieldLegend variant="label">{t("form.klassen")}</FieldLegend>
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${id}-alle-klassen`}
          checked={alleKlassen}
          onCheckedChange={(checked) => setAlleKlassen(checked === true)}
        />
        <Label htmlFor={`${id}-alle-klassen`}>{t("form.alleKlassen")}</Label>
      </div>
      <FieldDescription>{t("form.alleKlassenUitleg")}</FieldDescription>

      {!alleKlassen && (
        <>
          <SelectAllCheckbox
            id={`${id}-klassen-alles`}
            total={(klassen ?? []).length}
            selected={gekozenKlassen.length}
            onChange={(alles) =>
              setKlasseIds(
                alles ? (klassen ?? []).map((klasse) => klasse.id) : [],
              )
            }
          />
          <ul className="grid gap-2 sm:grid-cols-2">
            {(klassen ?? []).map((klasse) => (
              <li key={klasse.id}>
                <CheckboxCard
                  id={`${id}-klasse-${klasse.id}`}
                  title={klasse.name}
                  checked={klasseIds.includes(klasse.id)}
                  onCheckedChange={() =>
                    setKlasseIds((huidig) => toggle(huidig, klasse.id))
                  }
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </FieldSet>
  );
}
